import isEqual from 'lodash.isequal';
import React, { useRef } from 'react';
import { createUseStyles } from 'react-jss';
var createMemoStyles = function (styles, options) {
  var useStyles = createUseStyles(styles, options);
  var useMemoStyles = function (data) {
    var ref = useRef(data);
    if (!ref.current || !isEqual(data, ref.current)) {
      ref.current = data;
    }
    return useStyles(ref.current);
  };
  return useMemoStyles;
};

/// ///////////////////////// GENERIC START ////////////////////////////
var SIZES = ['xs', 'sm', 'md', 'lg', 'xl'];
/// ///////////////////////// PALETTE START ////////////////////////////
var SEMANTIC_COLORS = ['primary', 'secondary', 'success', 'warning', 'upgrade', 'error', 'info', 'muted'];
var LINK_COLORS = ['link'];
var TYPE_COLORS = ['dark', 'light', 'darkMuted', 'lightMuted', 'link'];
var BACKGROUND_COLORS = ['main', 'light', 'dark', 'overlay', 'accent'];
var BACKGROUND_SEMANTIC_COLORS = ['primary', 'success', 'warning', 'error', 'info', 'upgrade', 'muted'];
var BORDER_COLORS = ['main', 'active', 'hover', 'muted'];
var DATA_COLORS = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
/// ///////////////////////// FONT TYPE START ////////////////////////////
var FONT_FAMILY_KEYS = ['base', 'headings', 'icon'];
var FONT_SIZES = ['caption', 'bodySm', 'body', 'cardTitle', 'sectionTitleSm', 'sectionTitle', 'pageTitle', 'hero2', 'hero1'];
var FONT_WEIGHTS = ['light', 'regular', 'medium'];
var FONT_ASSETS = ['base', 'path', 'name', 'versions'];
/// ///////////////////////// SPACING START ////////////////////////////
var SPACING_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
var BORDER_KEYS = ['main', 'hover', 'active', 'muted', 'dark', 'light'];
/// ///////////////////////// RADIUS START ////////////////////////////
var RADIUS_KEYS = ['zero', 'small', 'medium', 'large'];
/// ///////////////////////// SHADOW START ////////////////////////////
var SHADOW_KEYS = ['none', 'sky', 'space'];
/// ///////////////////////// ELEVATION START ////////////////////////////
var ELEVATION_KEYS = ['none', 'sky', 'space'];
/// ///////////////////////// DATA VIS ////////////////////////////
var dataTransform = function (arr) {
  return Object.freeze(arr.map(function (x) {
    return "data-".concat(x);
  }));
};
var DATA_VIS_COLORS = dataTransform(DATA_COLORS);
var getDataColor = function (s) {
  return s.split('-')[1];
};
/// ///////////////////////// HELPERS ////////////////////////////
var readOnlyIncludes = function (arr, el) {
  return arr.includes(el);
};
/// ///////////////////////// TYPE GUARDS ////////////////////////////
var isColorShape = function (c) {
  return typeof c === 'object' && 'main' in c;
};
var isSemanticColor = function (c) {
  return readOnlyIncludes(SEMANTIC_COLORS, c);
};
var isLinkColor = function (c) {
  return c === 'link';
};
var isTypeColor = function (c) {
  return readOnlyIncludes(TYPE_COLORS, c);
};
var isBackgroundColor = function (c) {
  return readOnlyIncludes(BACKGROUND_COLORS, c);
};
var isBackgroundSemanticColor = function (c) {
  return readOnlyIncludes(BACKGROUND_SEMANTIC_COLORS, c);
};
var isBorderColor = function (c) {
  return readOnlyIncludes(BORDER_COLORS, c);
};
var isDataColor = function (c) {
  return readOnlyIncludes(DATA_COLORS, c);
};
var isDataVisColor = function (c) {
  return readOnlyIncludes(DATA_VIS_COLORS, c);
};
var getColor = function (palette, color, _a, onDark) {
  var _b = _a === void 0 ? {} : _a,
    _c = _b.semanticKey,
    semanticKey = _c === void 0 ? 'main' : _c,
    _d = _b.semanticBackgroundKey,
    semanticBackgroundKey = _d === void 0 ? 'light' : _d,
    _e = _b.colorType,
    colorType = _e === void 0 ? 'semantic' : _e,
    _f = _b.semanticOnDarkKey,
    semanticOnDarkKey = _f === void 0 ? 'onDark' : _f,
    fallback = _b.fallback;
  var c = fallback !== null && fallback !== void 0 ? fallback : 'currentcolor';
  if (!color) return c;
  var cType = colorType;
  // if passed a portion of the Pallette that is the semantic color use that.
  if (isColorShape(color)) {
    c = color[semanticKey];
  } else {
    // Check Semantic
    if (cType === 'semantic') {
      if (onDark && isSemanticColor(color)) {
        c = palette[color][semanticOnDarkKey];
      } else if (isSemanticColor(color)) {
        c = palette[color][semanticKey];
      } else {
        cType = 'link';
      }
    }
    // Check type and get Color
    if (cType === 'link') {
      if (isLinkColor(color)) {
        c = palette[color][semanticKey];
      } else {
        cType = 'text';
      }
    }
    if (cType === 'text') {
      if (isTypeColor(color)) {
        c = palette[cType][color];
      } else {
        cType = 'background';
      }
    }
    if (cType === 'background') {
      if (isBackgroundColor(color)) {
        c = palette[cType][color];
      } else if (isBackgroundSemanticColor(color)) {
        c = palette[cType][color][semanticBackgroundKey];
      } else {
        cType = 'border';
      }
    }
    if (cType === 'border') {
      if (isBorderColor(color)) {
        c = palette[cType][color];
      } else {
        cType = 'data';
      }
    }
    if (cType === 'data') {
      if (isDataVisColor(color)) {
        c = palette[cType][getDataColor(color)];
      } else if (isDataColor(color)) {
        c = palette[cType][color];
      }
    }
  }
  return c;
};
var name$1 = 'Wrench';
var basePath$1 = process.env.NODE_ENV !== 'production' ? 'cdn.mtassets.net' : 'cdn.smassets.net';
var fontSize = {
  caption: 12,
  bodySm: 14,
  body: 16,
  cardTitle: 20,
  sectionTitleSm: 24,
  sectionTitle: 28,
  pageTitle: 32,
  hero1: 48,
  hero2: 40
};
var fontWeight = {
  light: 300,
  regular: 400,
  medium: 500
};
var fontFamily = {
  base: "'National 2', 'Helvetica Neue', Helvetica, Arial, 'Hiragino Sans', 'Hiragino Kaku Gothic Pro', '游ゴシック', '游ゴシック体', YuGothic, 'Yu Gothic', 'ＭＳ ゴシック', 'MS Gothic', sans-serif",
  headings: "'National 2', 'Helvetica Neue', Helvetica, Arial, 'Hiragino Sans', 'Hiragino Kaku Gothic Pro', '游ゴシック', '游ゴシック体', YuGothic, 'Yu Gothic', 'ＭＳ ゴシック', 'MS Gothic', sans-serif",
  icon: 'Mateo'
};
var assets = {
  base: "https://".concat(basePath$1, "/assets/wds-core_4_20_1"),
  path: '/fonts/National2Web',
  name: "'National 2'",
  version: 2
};
var type$1 = {
  assets: assets,
  fontFamily: fontFamily,
  fontSize: fontSize,
  fontWeight: fontWeight
};
var palette$1 = {
  transparent: {
    clear: 'transparent',
    dark: 'rgba(51,62,72,0.2)',
    light: 'rgba(255, 255, 255, 0.2)'
  },
  primary: {
    main: '#00BF6F',
    contrast: '#FFFFFF',
    onDark: '#00BF6F'
  },
  secondary: {
    main: '#6B787F',
    contrast: '#FFFFFF',
    onDark: '#D0D2D3'
  },
  success: {
    main: '#00BF6F',
    contrast: '#FFFFFF',
    onDark: '#00BF6F'
  },
  warning: {
    main: '#F05B24',
    contrast: '#FFFFFF',
    onDark: '#F48B64'
  },
  error: {
    main: '#AC145A',
    contrast: '#FFFFFF',
    onDark: '#D993B3'
  },
  info: {
    main: '#2DCCD3',
    contrast: '#333E48',
    onDark: '#2DCCD3'
  },
  upgrade: {
    main: '#F9BE00',
    contrast: '#333E48',
    onDark: '#F9BE00'
  },
  muted: {
    main: '#D0D2D3',
    contrast: '#333E48',
    onDark: '#D0D2D3'
  },
  link: {
    main: '#007FAA',
    contrast: '#FFFFFF',
    onDark: '#63B1CB'
  },
  text: {
    dark: '#333E48',
    light: '#FFFFFF',
    darkMuted: '#6B787F',
    lightMuted: '#D0D2D3',
    link: '#007FAA'
  },
  background: {
    main: '#F7F8FA',
    light: '#FFFFFF',
    dark: '#333E48',
    overlay: 'rgba(107, 120, 127, .86)',
    accent: '#EDEEEE',
    primary: {
      light: '#E5F9F1',
      medium: '#ADEBD1'
    },
    success: {
      light: '#E5F9F1',
      medium: '#ADEBD1'
    },
    warning: {
      light: '#FDEBE5',
      medium: '#FBD8CA'
    },
    error: {
      light: '#F5E3EB',
      medium: '#EBC7D7'
    },
    info: {
      light: '#E6F9FA',
      medium: '#CDF3F4'
    },
    upgrade: {
      light: '#FEF2CC',
      medium: '#FDE599'
    },
    muted: {
      light: '#EDEEEE',
      medium: '#EDEEEE'
    }
  },
  border: {
    main: '#D0D2D3',
    active: '#404040',
    hover: '#757575',
    muted: '#EDEEEE'
  },
  data: {
    one: '#507CB6',
    two: '#00BF6F',
    three: '#6BC8CD',
    four: '#F9BE00',
    five: '#7D5E90',
    six: '#DB4D5C',
    seven: '#768086',
    eight: '#D25F90',
    nine: '#A38364',
    ten: '#FF8B4F'
  }
};
var spacing$1 = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 24,
  6: 32,
  7: 48,
  8: 64,
  9: 96,
  10: 128
};
var motion$1 = {
  curve: {
    enter: 'cubic-bezier(0, 0, 0, 1)',
    exit: 'cubic-bezier(1, 0, 1, 1)',
    ease: 'cubic-bezier(.2, 0, .1, 1)',
    easeReverse: 'cubic-bezier(.9, 0, .8, 1)',
    bounceStart: 'cubic-bezier(0, 0, .1, 1)',
    bounceEnd: 'cubic-bezier(.2, 0, 0, 1)'
  },
  fade: {
    speed: {
      slow: 300,
      normal: 200,
      fast: 100
    }
  },
  slide: {
    speed: {
      slow: 450,
      normal: 350,
      fast: 250
    },
    distance: {
      normal: 25,
      near: 5,
      far: 75
    }
  }
};
var border$1 = {
  main: [1, 'solid', getColor(palette$1, 'main', {
    colorType: 'border'
  })],
  hover: [1, 'solid', getColor(palette$1, 'hover', {
    colorType: 'border'
  })],
  active: [1, 'solid', getColor(palette$1, 'active', {
    colorType: 'border'
  })],
  muted: [1, 'solid', getColor(palette$1, 'muted', {
    colorType: 'border'
  })],
  dark: [1, 'solid', getColor(palette$1, 'dark', {
    colorType: 'background'
  })],
  light: [2, 'solid', getColor(palette$1, 'light', {
    colorType: 'background'
  })]
};
var radius$1 = {
  zero: 0,
  small: 4,
  medium: 24,
  large: '50%'
};
var shadow$1 = {
  none: 'none',
  sky: '0 2px 4px 0 rgba(0, 0, 0, 0.14)',
  space: '0 2px 8px 0 rgba(0,0,0,0.14)'
};
var breakpoints$1 = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200
};
var iconSize$1 = {
  sm: 13,
  md: 16,
  lg: 24,
  xl: 32
};
var elevation$1 = {
  none: 0,
  sky: 999,
  space: 9999
};
/**
 * Exported theme is the Surveymonkey theme.
 * See our storybook for more info on the theme shape/breakdown
 * @see {@link [Storybook](http://storybook.jungle.tech/wrench/index.html?path=/story/intro-theme-breakdown--page)}
 */
var theme$1 = {
  name: name$1,
  type: type$1,
  palette: palette$1,
  spacing: spacing$1,
  motion: motion$1,
  border: border$1,
  radius: radius$1,
  shadow: shadow$1,
  breakpoints: breakpoints$1,
  iconSize: iconSize$1,
  elevation: elevation$1
};
var name = 'Elevate';
var basePath = process.env.NODE_ENV !== 'production' ? 'cdn.mtassets.net' : 'cdn.smassets.net';
var type = {
  fontFamily: {
    base: "'National 2', 'Helvetica Neue', Helvetica, Arial, 'Hiragino Sans', 'Hiragino Kaku Gothic Pro', '游ゴシック', '游ゴシック体', YuGothic, 'Yu Gothic', 'ＭＳ ゴシック', 'MS Gothic', sans-serif",
    headings: "'National 2', 'Helvetica Neue', Helvetica, Arial, 'Hiragino Sans', 'Hiragino Kaku Gothic Pro', '游ゴシック', '游ゴシック体', YuGothic, 'Yu Gothic', 'ＭＳ ゴシック', 'MS Gothic', sans-serif",
    icon: 'Mateo'
  },
  fontSize: {
    caption: 12,
    bodySm: 14,
    body: 16,
    cardTitle: 20,
    sectionTitleSm: 24,
    sectionTitle: 28,
    pageTitle: 32,
    hero1: 48,
    hero2: 40
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500
  },
  assets: {
    base: "https://".concat(basePath, "/assets/wds-core_4_20_1"),
    path: '/fonts/National2Web',
    name: "'National 2'",
    version: 2
  }
};
var palette = {
  transparent: {
    clear: 'transparent',
    dark: 'rgba(64,64,64,0.2)',
    light: 'rgba(255, 255, 255, 0.2)'
  },
  primary: {
    main: '#008323',
    contrast: '#FFFFFF',
    onDark: '#73BB86'
  },
  secondary: {
    main: '#757575',
    contrast: '#FFFFFF',
    onDark: '#D0D0D0'
  },
  success: {
    main: '#008323',
    contrast: '#FFFFFF',
    onDark: '#73BB86'
  },
  warning: {
    main: '#F7B500',
    contrast: '#404040',
    onDark: '#FAD366'
  },
  error: {
    main: '#CC0000',
    contrast: '#FFFFFF',
    onDark: '#EA9494'
  },
  info: {
    main: '#2CB2F1',
    contrast: '#000000',
    onDark: '#56C1F4'
  },
  upgrade: {
    main: '#FFE11B',
    contrast: '#404040',
    onDark: '#FFF3A4'
  },
  muted: {
    main: '#D0D0D0',
    contrast: '#404040',
    onDark: '#D0D0D0'
  },
  link: {
    main: '#0A7CC1',
    contrast: '#FFFFFF',
    onDark: '#71B3DB'
  },
  text: {
    dark: '#404040',
    light: '#FFFFFF',
    darkMuted: '#757575',
    lightMuted: '#EFEFEF',
    link: '#0A7CC1'
  },
  background: {
    main: '#f7f7f7',
    light: '#ffffff',
    dark: '#404040',
    overlay: 'rgba(117, 117, 117, .86)',
    accent: '#EFEFEF',
    primary: {
      light: '#E5F3E9',
      medium: '#C2E1CA'
    },
    success: {
      light: '#E5F3E9',
      medium: '#C2E1CA'
    },
    warning: {
      light: '#FFEEC0',
      medium: '#FCE39E'
    },
    error: {
      light: '#FBEAEA',
      medium: '#F4C7C7'
    },
    info: {
      light: '#DCF0FF',
      medium: '#C4E0F0'
    },
    upgrade: {
      light: '#FFF9D1',
      medium: '#FFF3A4'
    },
    muted: {
      light: '#EFEFEF',
      medium: '#EFEFEF'
    }
  },
  border: {
    main: '#d0d0d0',
    active: '#404040',
    hover: '#757575',
    muted: '#EDEEEE'
  },
  data: {
    one: '#507CB6',
    two: '#00BF6F',
    three: '#6BC8CD',
    four: '#F9BE00',
    five: '#7D5E90',
    six: '#DB4D5C',
    seven: '#768086',
    eight: '#D25F90',
    nine: '#A38364',
    ten: '#FF8B4F'
  }
};
var spacing = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 24,
  6: 32,
  7: 48,
  8: 64,
  9: 96,
  10: 128
};
var motion = {
  curve: {
    enter: 'cubic-bezier(0, 0, 0, 1)',
    exit: 'cubic-bezier(1, 0, 1, 1)',
    ease: 'cubic-bezier(.2, 0, .1, 1)',
    easeReverse: 'cubic-bezier(.9, 0, .8, 1)',
    bounceStart: 'cubic-bezier(0, 0, .1, 1)',
    bounceEnd: 'cubic-bezier(.2, 0, 0, 1)'
  },
  fade: {
    speed: {
      slow: 300,
      normal: 200,
      fast: 100
    }
  },
  slide: {
    speed: {
      slow: 450,
      normal: 350,
      fast: 250
    },
    distance: {
      normal: 25,
      near: 5,
      far: 75
    }
  }
};
getColor(palette, 'main', {
  colorType: 'border'
});
var border = {
  main: [1, 'solid', getColor(palette, 'main', {
    colorType: 'border'
  })],
  hover: [1, 'solid', getColor(palette, 'hover', {
    colorType: 'border'
  })],
  active: [1, 'solid', getColor(palette, 'active', {
    colorType: 'border'
  })],
  muted: [1, 'solid', getColor(palette, 'muted', {
    colorType: 'border'
  })],
  dark: [1, 'solid', getColor(palette, 'dark', {
    colorType: 'background'
  })],
  light: [2, 'solid', getColor(palette, 'light', {
    colorType: 'background'
  })]
};
var radius = {
  zero: 0,
  small: 4,
  medium: 24,
  large: '50%'
};
var shadow = {
  none: 'none',
  sky: '0 2px 4px 0 rgba(0, 0, 0, 0.14)',
  space: '0 2px 8px 0 rgba(0, 0, 0, 0.14)'
};
var breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200
};
var iconSize = {
  sm: 13,
  md: 16,
  lg: 24,
  xl: 32
};
var elevation = {
  none: 0,
  sky: 999,
  space: 9999
};
/**
 * Exported theme is the Momentive theme.
 * See our storybook for more info on the theme shape/breakdown
 * @see {@link [Storybook](http://storybook.jungle.tech/wrench/index.html?path=/story/intro-theme-breakdown--page)}
 */
var theme = {
  name: name,
  type: type,
  palette: palette,
  spacing: spacing,
  motion: motion,
  border: border,
  radius: radius,
  shadow: shadow,
  breakpoints: breakpoints,
  iconSize: iconSize,
  elevation: elevation
};
var useStyles = createMemoStyles(function (_a) {
  var _b = _a.type,
    assets = _b.assets,
    fontFamily = _b.fontFamily,
    fontWeight = _b.fontWeight,
    fontSize = _b.fontSize;
  var fontPath = "".concat(assets.base).concat(assets.path);
  var fontFaces = FONT_WEIGHTS.map(function (name) {
    // we need this because the font file names are all National2-<Weight>.2.ext
    var upperCaseName = "".concat(name[0].toUpperCase()).concat(name.slice(1));
    var weight = fontWeight[name];
    return {
      fontFamily: assets.name,
      fontWeight: weight,
      fontDisplay: 'swap',
      src: "url(".concat(fontPath, "-").concat(upperCaseName, ".").concat(assets.version, ".woff2) format('woff2')"),
      fallbacks: [{
        src: "url(".concat(fontPath, "-").concat(upperCaseName, ".").concat(assets.version, ".woff) format('woff')")
      }, {
        src: "url(".concat(fontPath, "-").concat(upperCaseName, ".").concat(assets.version, ".eot#iefix) format('embedded-opentype')")
      }]
    };
  });
  return {
    '@font-face': fontFaces,
    '@global': {
      "[class*='wrench'], [class*='wrench']::before, [class*='wrench']::after": {
        fontFamily: fontFamily.base,
        '-webkit-font-smoothing': 'antialiased',
        boxSizing: 'border-box'
      },
      body: {
        fontSize: fontSize.body
      }
    }
  };
});
var GlobalStyles = function () {
  useStyles();
  // return null here for linting rule requirements
  return React.createElement(React.Fragment, null, null);
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var blueprintBoxStyle = {
  border: '2px dashed #2dccd3',
  backgroundColor: 'rgba(45,204,211,.6)',
  padding: '16px',
  fontFamily: 'Roboto, Helvetica, Arial'
};
var blueprintBoxStyleXl = {
  border: '2px dashed #2dccd3',
  backgroundColor: 'rgba(45,204,211,.6)',
  padding: '40px 16px'
};
var innerBoxStyle = {
  border: '2px dashed #2dccd3',
  backgroundColor: 'rgba(45,204,211,.6)',
  fontFamily: 'Roboto, Helvetica, Arial'
};
var outerBoxStyle = __assign(__assign({}, innerBoxStyle), {
  marginBottom: '16px'
});
var paddingBoxStyle = __assign(__assign({}, innerBoxStyle), {
  backgroundColor: '#ffffff',
  border: 'none'
});
var flexBoxStyle = __assign(__assign({}, innerBoxStyle), {
  padding: 8,
  margin: 8
});
var flexBoxStyleSm = __assign(__assign({}, innerBoxStyle), {
  padding: 8,
  margin: 8,
  height: 40
});
var flexBoxStyleMd = __assign(__assign({}, innerBoxStyle), {
  padding: 8,
  margin: 8,
  height: 80
});
var flexBoxStyleLg = __assign(__assign({}, innerBoxStyle), {
  padding: 8,
  margin: 8,
  height: 120
});
export { BACKGROUND_COLORS, BACKGROUND_SEMANTIC_COLORS, BORDER_COLORS, BORDER_KEYS, DATA_COLORS, DATA_VIS_COLORS, ELEVATION_KEYS, theme as ElevateTheme, FONT_ASSETS, FONT_FAMILY_KEYS, FONT_SIZES, FONT_WEIGHTS, GlobalStyles, LINK_COLORS, RADIUS_KEYS, SEMANTIC_COLORS, SHADOW_KEYS, SIZES, SPACING_KEYS, TYPE_COLORS, theme$1 as WrenchTheme, blueprintBoxStyle, blueprintBoxStyleXl, createMemoStyles, flexBoxStyle, flexBoxStyleLg, flexBoxStyleMd, flexBoxStyleSm, getColor, getDataColor, innerBoxStyle, isBackgroundColor, isBackgroundSemanticColor, isBorderColor, isColorShape, isDataColor, isDataVisColor, isLinkColor, isSemanticColor, isTypeColor, outerBoxStyle, paddingBoxStyle };